// @ts-ignore
import { formatDateTime, formatDateTimeNoTimeZoneForReport, formatDate, timeFromDate, formatDateTimeTotoLocaleString, formatDateTimeET } from "@/src/date-helpers";

export type DisplayableType = {
  value: string;
  display: string;
}

/**
 * This class provides a base class to handle working in JSON coming in from API requests
 * To create a UI model, extend `APIModel` and define a static factory method
 * (e.g. `buildFromAPI`) that uses the inherited mapping helpers to transform raw
 * API response data into strongly-typed, display-ready properties.
 */
export class APIModel { 
  /**
   * Maps fields directly from the source object to the target object without any transformation.
   *
   *
   * @param fieldsToMap - Array of field names to copy from the source object.
   * @param from - The source object (typically raw API response JSON).
   * @param to - The target model instance to populate.
   */
  static mapFieldsDirectly<T>(fieldsToMap: Array<string>, from: any, to: T) {
    fieldsToMap.forEach((k) => {
      to[k] = from[k];
    });
  }

  /**
   * Maps boolean fields to a {@link DisplayableType} with "Yes", "No", or an empty string.
   *
   * @param fieldsToMap - Array of field names containing boolean values.
   * @param from - The source object (typically raw API response JSON).
   * @param to - The target model instance to populate. Each field is set to `{ value, display }`.
   */
  static mapBooleanYesNo<T>(fieldsToMap: Array<string>, from: any, to: T) {
    fieldsToMap.forEach((k) => {
      const val = from[k];
      let display = '';
      if (val === true) {
        display = 'Yes'
      } else if (val === false) {
        display = 'No'
      } else {
        display = ''
      }

      to[k] = { value: val, display: display };
    });
  }

  /**
   * Maps date-time fields to a {@link DisplayableType} with a formatted display string.
   * When a `time_zone` is provided the display includes the time zone label;
   * otherwise the date-time is formatted without a time zone (suitable for reports).
   * Falls back to `"N/A"` when the formatted result is empty.
   *
   * @param fieldsToMap - Array of field names containing date-time values.
   * @param from - The source object (typically raw API response JSON).
   * @param to - The target model instance to populate. Each field is set to `{ value, display }`.
   * @param time_zone - Optional time zone label to append to the formatted date-time.
   */
  static mapFormatDateTime<T>(fieldsToMap: Array<string>, from: any, to: T, time_zone?: any) {
    fieldsToMap.forEach((k) => {
      const val = from[k];
      let display;
      if (!time_zone) {
        display = val ? formatDateTimeNoTimeZoneForReport(val) : '';
      } else {
        display = val ? formatDateTime(val) + ` (${time_zone})` : '';
      }


      to[k] = { value: val, display: display || "N/A" };
    });

  }

  /**
   * Maps date fields to a {@link DisplayableType} with a formatted date-only display string.
   *
   * @param fieldsToMap - Array of field names containing date values.
   * @param from - The source object (typically raw API response JSON).
   * @param to - The target model instance to populate. Each field is set to `{ value, display }`.
   */
  static mapFormatDate<T>(fieldsToMap: Array<string>, from: any, to: T) {
    fieldsToMap.forEach((k) => {
      const val = from[k];
      const display = val ? formatDate(val) : '';


      to[k] = { value: val, display: display };
    });

  }

  /**
   * Extracts and maps the time portion from a date value to a {@link DisplayableType}.
   * Note: the entire `from` object is treated as the date value (not a keyed field).
   *
   * @param fieldsToMap - Array of field names to set on the target object.
   * @param from - The date value to extract the time from.
   * @param to - The target model instance to populate. Each field is set to `{ value, display }`.
   */
  static mapTimeFromDate<T>(fieldsToMap: Array<string>, from: any, to: T) {
    fieldsToMap.forEach((k) => {
      const val = from;
      const display = val ? timeFromDate(val) : '';


      to[k] = { value: val, display: display };
    });

  }

  /**
   * Maps a date to a {@link DisplayableType} using formatDateTimeET display.
   * Uses `date1` if available, otherwise falls back to `date2`.
   *
   * @param fieldsToMap - Array of field names to set on the target object.
   * @param from - The source object (e.g. update date).
   * @param to - The target model instance to populate. Each field is set to `{ value, display }`.
   */
  static mapFormatDateTimeET<T>(fieldsToMap: Array<string>, from: any, to: T) {
    fieldsToMap.forEach((k) => {
      const val = from[k];
      const display = val ? formatDateTimeET(val) + ' ET' : 'N/A';

      to[k] = { value: val, display: display };
    });
  }

  /**
   * Maps a "last updated" date to a {@link DisplayableType} using locale-formatted display.
   * Uses `date1` if available, otherwise falls back to `date2`.
   *
   * @param fieldsToMap - Array of field names to set on the target object.
   * @param date1 - The primary date value (e.g. update date).
   * @param date2 - The fallback date value (e.g. creation date).
   * @param to - The target model instance to populate. Each field is set to `{ value, display }`.
   */
  static mapLastUpdateDate<T>(fieldsToMap: Array<string>, date1: any, date2: any, to: T) {
    fieldsToMap.forEach((k) => {
      const val = date1 || date2;
      const display = val ? formatDateTimeTotoLocaleString(val) : '';

      to[k] = { value: val, display: display };
    });
  }

  /**
   * Maps fields directly from the source object, substituting `"N/A"` for any falsy value.
   *
   * @param fieldsToMap - Array of field names to copy from the source object.
   * @param from - The source object (typically raw API response JSON).
   * @param to - The target model instance to populate.
   */
  static mapFieldsNA<T>(fieldsToMap: Array<string>, from: any, to: T) {
    fieldsToMap.forEach((k) => {
      to[k] = from[k] || 'N/A';
    });
  }

  /**
   * Maps array fields to a flattened, human-readable string.
   * Nested arrays are joined with `", "` and top-level items are joined with `"; "`.
   * Non-array values result in an empty string.
   *
   * @param fieldsToMap - Array of field names containing array values.
   * @param from - The source object (typically raw API response JSON).
   * @param to - The target model instance to populate (set to the joined string).
   */
  static mapArrays<T>(fieldsToMap: Array<string>, from: any, to: T) {
    fieldsToMap.forEach((k) => {
      const val = from[k];
      let display = '';
      if (Array.isArray(val)) {
        display = val.map(v => {
          if (Array.isArray(v)) {
            return v.map(i => i?.toString().trim()).filter(Boolean).join(', ');
          }
          return v?.toString().trim();
        }).filter(Boolean).join('; ');
      } else {
        display = '';
      }

      to[k] = display;
    });
  }

  /**
   * Maps date-time fields to a {@link DisplayableType}, always including a time zone indicator.
   * When `time_zone` is provided the label is appended in parentheses;
   * otherwise the literal string `" null"` is appended.
   * Falls back to `"N/A"` when the formatted result is empty.
   *
   * @param fieldsToMap - Array of field names containing date-time values.
   * @param from - The source object (typically raw API response JSON).
   * @param to - The target model instance to populate. Each field is set to `{ value, display }`.
   * @param time_zone - Optional time zone label to append to the formatted date-time.
   */
  static mapFormatDateTimeWithTimezone<T>(fieldsToMap: Array<string>, from: any, to: T, time_zone?: any) {
    fieldsToMap.forEach((k) => {
      const val = from[k];
      let display;
      if (!time_zone) {
        display = val ? formatDateTime(val) + ' null' : '';
      } else {
        display = val ? formatDateTime(val) + ` (${time_zone})` : '';
      }


      to[k] = { value: val, display: display || "N/A" };
    });
  }
}